import React, { useEffect, useState } from 'react';
import { getStats, getSystems, getStravaStatus } from '../api';
import { Link } from 'react-router-dom';
import { Check, Mountain, Link2, RefreshCw } from 'lucide-react';

function StatCard({ label, value, sub }) {
  return (
    <div className="card stat-card">
      <div className="stat-card__value">
        {value}
      </div>
      <div className="stat-card__label">
        {label}
      </div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  );
}

function OnboardingStep({ number, title, description, done, active, last, children }) {
  const stepClass = `onboarding-step${last ? ' onboarding-step--last' : ''}${done && !active ? ' onboarding-step--done' : ''}`;
  const numberClass = `onboarding-step__number${done ? ' onboarding-step__number--done' : active ? ' onboarding-step__number--active' : ' onboarding-step__number--pending'}`;

  return (
    <div className={stepClass}>
      <div className={numberClass}>
        {done ? <Check size={16} strokeWidth={3} /> : number}
      </div>
      <div className="flex-1">
        <div className="onboarding-step__title">
          {title}
        </div>
        <div className={`onboarding-step__desc${active && children ? ' onboarding-step__desc--spaced' : ''}`}>
          {description}
        </div>
        {active && children}
      </div>
    </div>
  );
}

function Onboarding({ hasSystems, stravaConnected, backfillComplete, onSkip }) {
  const step1Done = hasSystems;
  const step2Done = stravaConnected;
  const step3Done = backfillComplete;
  const activeStep = !step1Done ? 1 : !step2Done ? 2 : !step3Done ? 3 : null;

  return (
    <div className="fade-in">
      <div className="page-header page-header--lg">
        <h1 className="page-header__title">WELCOME TO NOTCHED</h1>
        <p className="page-header__subtitle">Let's get your trails set up. It only takes a minute.</p>
      </div>

      <div className="card" style={{ padding: '8px 20px' }}>
        <OnboardingStep
          number={1} title="ADD YOUR FIRST TRAIL SYSTEM"
          description="Search for a trail area and import all its trails."
          done={step1Done} active={activeStep === 1}
        >
          <Link to="/systems" className="btn btn--primary">
            <Mountain size={14} /> Find Trails
          </Link>
        </OnboardingStep>

        <OnboardingStep
          number={2} title="CONNECT STRAVA"
          description="Link your Strava account to auto-detect completed trails from your rides."
          done={step2Done} active={activeStep === 2}
        >
          <Link to="/settings" className="btn btn--primary">
            <Link2 size={14} /> Go to Settings
          </Link>
        </OnboardingStep>

        <OnboardingStep
          number={3} title="SYNC YOUR RIDES"
          description="Import your ride history and watch your trails light up."
          done={step3Done} active={activeStep === 3} last
        >
          <Link to="/settings" className="btn btn--primary">
            <RefreshCw size={14} /> Go to Settings
          </Link>
        </OnboardingStep>
      </div>

      {hasSystems && (
        <button
          onClick={onSkip}
          className="onboarding__skip"
        >
          Skip — I'll track trails manually
        </button>
      )}

      <p className={`onboarding__hint${hasSystems ? ' onboarding__hint--sm' : ''}`}>
        Don't use Strava? No problem — you can track trails manually by checking them off.
      </p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [systems, setSystems] = useState([]);
  const [strava, setStrava] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skippedOnboarding, setSkippedOnboarding] = useState(() => localStorage.getItem('notched_skip_onboarding') === '1');

  useEffect(() => {
    Promise.all([getStats(), getSystems(), getStravaStatus()])
      .then(([statsRes, systemsRes, stravaRes]) => {
        setStats(statsRes.data);
        setSystems(systemsRes.data);
        setStrava(stravaRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="loading">
      <div className="spinner spinner--lg" />
    </div>
  );

  const hasSystems = systems.length > 0;
  const stravaConnected = strava?.connected;
  const backfillComplete = strava?.backfillComplete;
  const onboardingComplete = hasSystems && (stravaConnected && backfillComplete || skippedOnboarding);

  function handleSkipOnboarding() {
    localStorage.setItem('notched_skip_onboarding', '1');
    setSkippedOnboarding(true);
  }

  // Show onboarding if any step is incomplete and not skipped
  if (!onboardingComplete) {
    return <Onboarding hasSystems={hasSystems} stravaConnected={stravaConnected} backfillComplete={backfillComplete} onSkip={handleSkipOnboarding} />;
  }

  return (
    <div className="fade-in">
      <div className="page-header page-header--lg">
        <h1 className="page-header__title">YOUR TRAILS</h1>
        <p className="page-header__subtitle">Track every trail you've shredded.</p>
      </div>

      {stats && (
        <div className="stats-grid">
          <StatCard label="TRAIL SYSTEMS" value={stats.totalSystems} />
          <StatCard label="TRAILS RIDDEN" value={stats.completedTrails} sub={`of ${stats.totalTrails} total`} />
          <StatCard label="COMPLETION" value={`${stats.completionRate}%`} />
          <StatCard label="KM RIDDEN" value={stats.completedDistance} sub={`of ${stats.totalDistance} km total`} />
        </div>
      )}

      {stats && stats.totalTrails > 0 && (
        <div className="card mb-xl">
          <div className="progress-section__header">
            <span className="progress-section__label">OVERALL PROGRESS</span>
            <span className="progress-section__value">{stats.completionRate}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar__fill" style={{ width: `${stats.completionRate}%` }} />
          </div>
        </div>
      )}

      <div className="section-header">
        <h2 className="section-header__title">RECENT SYSTEMS</h2>
        <Link to="/systems" className="btn btn--secondary btn--sm">View All →</Link>
      </div>

      <div className="grid grid--gap-lg">
        {systems.slice(0, 5).map(system => {
          const total = system.trails?.length || 0;
          const done = system.trails?.filter(t => t.completed).length || 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <Link to={`/systems/${system._id}`} key={system._id}>
              <div className="card system-card system-card--link">
                <div className="flex-1">
                  <div className="system-card__name--sm">{system.name}</div>
                  <div className="system-card__meta">
                    {system.region}{system.country ? `, ${system.country}` : ''}
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="system-card__pct">
                  <div className="system-card__pct-value">{pct}%</div>
                  <div className="system-card__pct-label">{done}/{total} trails</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
